'use client';

import { useEffect, useCallback, useState, useRef, useContext } from 'react';
import useSWR from 'swr';
import Cookies from 'js-cookie';
import { FlagshipContext } from '@flagship.io/react-sdk';
import { useRequestQueue } from '@web/queue/context';
import { ApiResult, useService } from '../api';
import { useCampaign } from '../campaign';
import { useExperiment } from '../experiments/useExperiment';
import {
  CHECKOUT_NEXT_BOX_PRICE,
  CHECKOUT_GWP_CALLOUT,
} from '../experiments/constants';
import { shared } from '../config';
import { ShopifyCartService } from './services';

export const useShopifyCart = ({
  shouldCreateCart = true,
  hasCheckedPath = false,
} = {}) => {
  const service = useService(ShopifyCartService);
  const { queueRequest } = useRequestQueue();
  const { campaignId } = useCampaign();
  const { state: flagshipState } = useContext(FlagshipContext);
  const [cartId, setCartId] = useState(null);
  const [isFetchingOfferFlags, setIsFetchingOfferFlags] = useState(true);
  const creatingRef = useRef(null);
  const cartIdRef = useRef(cartId);
  const flagsFetchedForRef = useRef(null);

  const { value: showCheckoutNextBoxPrice } = useExperiment(
    CHECKOUT_NEXT_BOX_PRICE,
    false,
  );
  const { value: showCheckoutGWPCallout } = useExperiment(
    CHECKOUT_GWP_CALLOUT,
    false,
  );

  // Keep cartIdRef.current updated with the latest cartId
  cartIdRef.current = cartId;

  const updateCartId = useCallback(
    newId => {
      if (newId && newId !== cartId) {
        setCartId(newId);
        Cookies.set('shopifyCartId', newId, { path: '/', expires: 30 });
      }
    },
    [cartId],
  );

  const applyStoredOfferCode = useCallback(
    async newCartId => {
      const storedOfferCode = Cookies.get('offerCode');
      if (!storedOfferCode) {
        // No offer code — apply DEFAULT as baseline so the cart is never empty,
        // preventing the offer context from applying it later and causing a second SWR cycle.
        Cookies.set('offerCode', shared.DEFAULT_OFFER_CODE, { path: '/' });
        await service.addPromo(newCartId, shared.DEFAULT_OFFER_CODE);
        return;
      }
      try {
        await service.validatePromo(storedOfferCode);
      } catch (e) {
        // The stored code is invalid (expired, fake, etc.)
        // fallback to DEFAULT offer code
        const defaultCode = shared.DEFAULT_OFFER_CODE || 'DEFAULT';
        Cookies.set('offerCode', defaultCode, { path: '/' });
        await service.addPromo(newCartId, defaultCode);
        return;
      }
      try {
        await service.addPromo(newCartId, storedOfferCode);
      } catch (e) {
        // Non-fatal
      }
    },
    [service],
  );

  const createCart = useCallback(async () => {
    if (cartIdRef.current) return { cart_id: cartIdRef.current };

    //* If a creation is already in progress, return the existing promise
    if (creatingRef.current) {
      return creatingRef.current;
    }

    creatingRef.current = (async () => {
      try {
        const response = await service.createCart();

        if (response?.data?.cart_id) {
          // Apply the stored offer code BEFORE setting cartId. Otherwise
          // updateCartId triggers the SWR fetch immediately and the cart
          // data lands in state without the coupon, which causes the
          // pages-router ShopifyOfferProvider to then apply DEFAULT over
          // the top. Applying first means the SWR fetch sees the cart
          // already couponed.
          await applyStoredOfferCode(response.data.cart_id);
          updateCartId(response.data.cart_id);
          return response.data;
        } else {
          return response?.data || null;
        }
      } catch (error) {
        creatingRef.current = null;
        return null;
      }
    })();

    return creatingRef.current;
  }, [service, updateCartId, applyStoredOfferCode]);

  const {
    data: cartData,
    error: cartError,
    isLoading: isShopifyCartLoading,
    mutate: refreshCart,
  } = useSWR(cartId ? `/api/v4/onboarding/${cartId}/` : null, null, {
    revalidateOnFocus: false,
    dedupingInterval: 10000,
    onSuccess: async data => {
      const flags = {
        showCheckoutNextBoxPrice,
        showCheckoutGWPCallout,
      };

      const abTestFlags = Object.entries(flags)
        .map(([key, value]) => `${key}:${value}`)
        .join(',');

      const attributes = {
        order_type: 'onboarding',
        cart_id: data.cart_id,
        ab_tests: abTestFlags,
      };

      queueRequest(() => updateCartAttributes(attributes, data.cart_id));
    },
    onError: async error => {
      if (error.response?.status === 404) {
        Cookies.remove('shopifyCartId', { path: '/' });
        setCartId(null);
        if (shouldCreateCart) {
          await createCart();
        }
      }
    },
    onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
      if (retryCount >= 1) return;
      setTimeout(() => revalidate({ retryCount }), 1000);
    },
  });

  const syncRecart = useCallback(
    id => {
      const sessionId = window._recart?.getSessionId?.();
      if (!sessionId || !id) return;
      service.syncRecartCart(sessionId, id).catch(err =>
        // eslint-disable-next-line no-console
        console.warn('Recart sync failed:', err),
      );
    },
    [service],
  );

  const addPetData = useCallback(
    async (petData = {}) => {
      let currentCartId = cartIdRef.current; // potential race condition handling with ref

      // If no cartId, try to create one
      if (!currentCartId) {
        const newCart = await createCart();
        currentCartId = newCart?.cart_id;
      }

      if (!currentCartId) {
        return null;
      }

      try {
        const response = await service.addPetData(currentCartId, {
          ...petData,
        });

        if (!currentCartId) {
          return null;
        }

        await refreshCart(response.data, false);
        return response?.data;
      } catch (error) {
        throw error;
      }
    },
    [service, refreshCart, createCart],
  );

  const deletePetFromCart = useCallback(
    async petId => {
      if (!cartId) return null;

      const response = await service.removePet(cartId, petId);
      await refreshCart(response.data, false);
      return response.data;
    },
    [service, cartId, refreshCart],
  );

  const addPromo = useCallback(
    async promoCode => {
      if (!cartId || cartError) return null;

      const response = await service.addPromo(cartId, promoCode);
      await refreshCart(response.data, false);
      return response.data;
    },
    [service, cartId, cartError, refreshCart],
  );

  const upsertMealPlan = useCallback(
    async (petId, planData = {}) => {
      if (!cartId) return null;
      return ApiResult.callAsync(async () => {
        const response = await service.upsertMealPlan(cartId, petId, planData);
        await refreshCart(response.data, false);
        syncRecart(cartId);
        return response.data;
      });
    },
    [service, cartId, refreshCart, syncRecart],
  );

  const attachExtras = useCallback(
    async (petId, extrasData = []) => {
      if (!cartId || !petId) return null;
      return ApiResult.callAsync(async () => {
        const response = await service.attachExtras(cartId, petId, extrasData);
        refreshCart(response.data, false);
        syncRecart(cartId);
        return response.data;
      });
    },
    [service, cartId, refreshCart, syncRecart],
  );

  const attachFreeGifts = useCallback(
    async (petId, giftData = {}) => {
      if (!cartId || !petId) return null;
      return ApiResult.callAsync(async () => {
        const response = await service.attachFreeGifts(cartId, petId, giftData);
        refreshCart(response.data, false);
        return response.data;
      });
    },
    [service, cartId, refreshCart],
  );

  const updateCartAttributes = useCallback(
    async (attributes = {}, idOverride = null) => {
      const id = idOverride || cartId;

      if (!id) {
        // eslint-disable-next-line no-console
        console.warn('updateCartAttributes: No Cart ID available');
        return null;
      }

      return ApiResult.callAsync(async () => {
        const response = await service.updateAttributes(id, attributes);
        await refreshCart(response.data, false);
        return response.data;
      });
    },
    [service, cartId, refreshCart],
  );

  useEffect(() => {
    //* I found that the createCart call being awaited was better for adding the attribute following.
    const initCart = async () => {
      const savedCartId = Cookies.get('shopifyCartId');

      if (savedCartId) {
        const urlOfferCode = new URLSearchParams(window.location.search).get(
          'offer_code',
        );
        if (urlOfferCode) {
          await applyStoredOfferCode(savedCartId);
        }
        setCartId(savedCartId);
      } else if (shouldCreateCart) {
        try {
          await createCart();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to initialize cart:', error);
        }
      }
    };

    initCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldCreateCart]);

  //* Update cart attributes with campaign_id & cart_id when cart is first created
  useEffect(() => {
    if (!cartId || !campaignId) return;
    const updateCampaign = async campaignId => {
      queueRequest(() =>
        updateCartAttributes({
          campaign_id: campaignId,
        }),
      );
    };
    updateCampaign(campaignId);
  }, [cartId, queueRequest, updateCartAttributes, campaignId]);

  const getCouponCode = useCallback(() => {
    if (!cartData?.coupon_code?.length) return null;
    return cartData.coupon_code[0];
  }, [cartData]);

  // Sync the offerCode cookie and Flagship visitor context with
  // the cart's current coupon code whenever it changes.
  useEffect(() => {
    if (!cartData || !cartId) return;

    const cartCouponCode = cartData?.coupon_code?.[0] ?? '';
    const cookieOfferCode = Cookies.get('offerCode') ?? '';

    // bail out if the URL carries an offer_code that hasn't been applied to the cart yet
    if (typeof window !== 'undefined') {
      const urlOfferCode =
        new URLSearchParams(window.location.search).get('offer_code') ?? '';
      if (urlOfferCode && urlOfferCode !== cartCouponCode) return;
    }

    const visitor = flagshipState?.visitor;

    if (
      visitor &&
      cartCouponCode &&
      flagsFetchedForRef.current !== cartCouponCode
    ) {
      flagsFetchedForRef.current = cartCouponCode;
      setIsFetchingOfferFlags(true);
      visitor.updateContext({ offerCode: cartCouponCode?.toLowerCase() });
      visitor.fetchFlags().finally(() => setIsFetchingOfferFlags(false));
    } else {
      setIsFetchingOfferFlags(false);
    }

    if (cartCouponCode === cookieOfferCode) return;

    if (cartCouponCode) {
      Cookies.set('offerCode', cartCouponCode, { path: '/' });
    } else {
      Cookies.remove('offerCode', { path: '/' });
    }
  }, [cartData, cartId, flagshipState?.visitor]);

  const isLoading =
    !hasCheckedPath ||
    isShopifyCartLoading ||
    (shouldCreateCart && !cartData && !cartError);

  return {
    cartId,
    cart: cartData || {},
    error: cartError,
    isLoading,
    isFetchingOfferFlags,
    createCart,
    addPetData,
    deletePetFromCart,
    addPromo,
    upsertMealPlan,
    attachExtras,
    attachFreeGifts,
    refreshCart,
    updateCartAttributes,
    couponCode: getCouponCode(),
  };
};
